<template>
  <OrganizationChart
    class="p-organizationchart"
    :class="{ 'p-organizationchart-selectable': selectionMode }"
    :value="adaptNodeToPrimeVue(modelValue)"
    :selection-mode="selectionMode"
    :selection="adaptSelectionToPrimeVue"
    :collapsible="collapsible"
    :collapsed-keys="collapsedKeys"
    role="tree"
    :aria-label="ariaLabel || t('mop.organizationChart.ariaLabel')"
    @node-select="onNodeSelect"
    @node-unselect="onNodeUnselect"
    @node-expand="onNodeExpand"
    @node-collapse="onNodeCollapse"
    @update:selection="$emit('update:selection', adaptSelectionFromPrimeVue($event))"
  >
    <template #default="slotProps">
      <slot name="node" v-bind="{ node: adaptNodeFromPrimeVue(slotProps.node) }">
        <div class="p-node-content">
          <div class="p-node-label">{{ slotProps.node.label }}</div>
        </div>
      </slot>
    </template>
  </OrganizationChart>

  <!-- Screen reader announcements -->
  <span ref="announcer" class="sr-only" aria-live="polite">
    {{ currentAnnouncement }}
  </span>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import type { PropType } from 'vue'
import type { OrganizationChartNode, OrganizationChartProps, OrganizationChartEmits } from './types'
import OrganizationChart from 'primevue/organizationchart'
import type { OrganizationChartNode as PrimeOrganizationChartNode } from 'primevue/organizationchart'
import { useLocalization } from '../../../../composables/useLocalization'

export default defineComponent({
  name: 'MOPOrganizationChart',
  components: {
    OrganizationChart
  },
  props: {
    modelValue: {
      type: Object as PropType<OrganizationChartNode>,
      required: true
    },
    selectionMode: {
      type: String as PropType<'single' | 'multiple' | undefined>,
      default: undefined
    },
    selection: {
      type: Array as PropType<OrganizationChartNode[]>,
      default: () => []
    },
    collapsible: {
      type: Boolean,
      default: false
    },
    collapsedKeys: {
      type: Object as PropType<Record<string, boolean>>,
      default: () => ({})
    },
    ariaLabel: {
      type: String,
      default: undefined
    },
    nodeAriaLabel: {
      type: String,
      default: undefined
    }
  },
  emits: [
    'update:selection',
    'node-select',
    'node-unselect',
    'node-expand',
    'node-collapse',
    'node-keydown',
    'update:collapsedKeys'
  ],
  setup(props) {
    const { t } = useLocalization()
    const currentAnnouncement = ref('')

    const adaptNodeToPrimeVue = (node: OrganizationChartNode): PrimeOrganizationChartNode => ({
      key: node.key,
      label: node.label,
      type: node.type,
      className: node.className,
      style: node.style,
      expanded: node.expanded,
      data: node.data,
      children: node.children?.map(adaptNodeToPrimeVue),
      selectable: node.selectable
    })

    const adaptNodeFromPrimeVue = (node: PrimeOrganizationChartNode): OrganizationChartNode => ({
      key: node.key?.toString() || '',
      label: node.label || '',
      type: node.type,
      className: node.className,
      style: node.style,
      expanded: node.expanded,
      data: node.data,
      children: node.children?.map(adaptNodeFromPrimeVue),
      selectable: node.selectable
    })

    const adaptSelectionToPrimeVue = computed(() =>
      props.selection.map(adaptNodeToPrimeVue)
    )

    const adaptSelectionFromPrimeVue = (selection: PrimeOrganizationChartNode[]) =>
      selection.map(adaptNodeFromPrimeVue)

    const announce = (message: string) => {
      currentAnnouncement.value = message
    }

    return {
      t,
      currentAnnouncement,
      announce,
      adaptNodeToPrimeVue,
      adaptNodeFromPrimeVue,
      adaptSelectionToPrimeVue,
      adaptSelectionFromPrimeVue
    }
  },
  methods: {
    onNodeSelect(node: PrimeOrganizationChartNode) {
      const adaptedNode = this.adaptNodeFromPrimeVue(node)
      this.$emit('node-select', adaptedNode)
      // Also emit update:selection for single selection mode
      if (this.selectionMode === 'single') {
        this.$emit('update:selection', [adaptedNode])
      }
      this.announce(this.t('mop.organizationChart.nodeSelected', { node: adaptedNode.label }))
    },
    onNodeUnselect(node: PrimeOrganizationChartNode) {
      const adaptedNode = this.adaptNodeFromPrimeVue(node)
      this.$emit('node-unselect', adaptedNode)
      this.announce(this.t('mop.organizationChart.nodeUnselected', { node: adaptedNode.label }))
    },
    onNodeExpand(node: PrimeOrganizationChartNode) {
      const adaptedNode = this.adaptNodeFromPrimeVue(node)
      this.$emit('node-expand', adaptedNode)
      this.announce(this.t('mop.organizationChart.nodeExpanded', { node: adaptedNode.label }))
    },
    onNodeCollapse(node: PrimeOrganizationChartNode) {
      const adaptedNode = this.adaptNodeFromPrimeVue(node)
      this.$emit('node-collapse', adaptedNode)
      this.announce(this.t('mop.organizationChart.nodeCollapsed', { node: adaptedNode.label }))
    }
  }
})
</script>

<style lang="scss">
.p-organizationchart {
  overflow: auto;
  padding: 1rem;

  .p-organizationchart-table {
    border-spacing: 0;
    border-collapse: separate;
    margin: 0 auto;
  }

  .p-organizationchart-node {
    padding: 0.5rem;
    border: 1px solid var(--surface-border);
    background: var(--surface-card);
    color: var(--text-color);
    border-radius: var(--border-radius);
    display: inline-block;
    position: relative;

    &:focus {
      outline: none;
      box-shadow: var(--focus-ring);
    }

    &.p-highlight {
      background: var(--primary-50);
      color: var(--primary-900);
    }

    &[aria-selected="true"] {
      background: var(--primary-50);
      color: var(--primary-900);
    }
  }

  .p-node-content {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  .p-node-toggler {
    cursor: pointer;
    position: absolute;
    bottom: -1.5rem;
    margin-left: -1rem;
    z-index: 2;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: var(--surface-card);
    border: 1px solid var(--surface-border);
    color: var(--text-color);
    display: flex;
    align-items: center;
    justify-content: center;

    &:focus {
      outline: none;
      box-shadow: var(--focus-ring);
    }

    .p-node-toggler-icon {
      font-size: 1rem;
    }

    &[aria-expanded="true"] .p-node-toggler-icon {
      transform: rotate(180deg);
    }
  }

  .p-organizationchart-line-down {
    height: 20px;
    width: 1px;
    background: var(--surface-border);
    margin: 0 auto;
  }

  .p-organizationchart-line-left,
  .p-organizationchart-line-right {
    border-radius: 0;

    &.p-organizationchart-line-top {
      border-top: 1px solid var(--surface-border);
    }
  }

  .p-organizationchart-line-left {
    border-right: 1px solid var(--surface-border);
  }

  .p-organizationchart-line-right {
    border-left: 1px solid var(--surface-border);
  }

  .p-organizationchart-nodes {
    padding: 1rem 0;
  }

  &.p-organizationchart-selectable {
    .p-node-content {
      cursor: pointer;

      &:focus {
        outline: none;
        box-shadow: var(--focus-ring);
      }
    }
  }

  // Screen reader only text
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

[dir="rtl"] .p-organizationchart {
  .p-node-toggler {
    margin-right: -1rem;
    margin-left: 0;
  }

  .p-organizationchart-line-left {
    border-left: 1px solid var(--surface-border);
    border-right: none;
  }

  .p-organizationchart-line-right {
    border-right: 1px solid var(--surface-border);
    border-left: none;
  }
}
</style>
